import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  FaPrint,
  FaTimes,
  FaFilePdf,
  FaHistory,
  FaImage,
  FaCalendarAlt,
  FaCheckCircle,
  FaArrowRight,
  FaInfoCircle,
  FaArrowLeft,
} from "react-icons/fa";
import "./ComplaintPrintExportModal.css";

const isJpgOrPng = (url) => {
  if (!url || typeof url !== "string") return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase().trim();
  return (
    clean.endsWith(".jpg") ||
    clean.endsWith(".jpeg") ||
    clean.endsWith(".png") ||
    url.startsWith("data:image/jpeg") ||
    url.startsWith("data:image/jpg") ||
    url.startsWith("data:image/png") ||
    /\.(jpe?g|png)($|\?)/i.test(url)
  );
};

const resolveFileUrl = (url, baseUrl = "http://localhost:5000") => {
  if (!url || typeof url !== "string") return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  const cleanBase = baseUrl.replace(/\/$/, "");
  const cleanPath = url.startsWith("/") ? url : `/${url}`;
  return `${cleanBase}${cleanPath}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatFullDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const calculateDuration = (startDate, endDate, status) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  let end = new Date();
  if (status && String(status).toLowerCase() === "completed" && endDate) {
    end = new Date(endDate);
  }
  if (isNaN(start) || isNaN(end) || end < start) return null;
  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

  const parts = [];
  if (diffDays > 0) parts.push(`${diffDays}d`);
  if (diffHrs > 0) parts.push(`${diffHrs}h`);
  if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
  return parts.join(" ");
};

const getStatusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completed") {
    return {
      className: "status-badge-completed",
      label: "Completed",
    };
  }
  if (s === "pending") {
    return {
      className: "status-badge-pending",
      label: "Pending",
    };
  }
  if (
    s.includes("accept") ||
    s.includes("solve") ||
    s.includes("review") ||
    s.includes("running") ||
    s.includes("checking")
  ) {
    return {
      className: "status-badge-review",
      label: status || "Under Review",
    };
  }
  return {
    className: "status-badge-default",
    label: status || "Open",
  };
};

const ComplaintPrintExportModal = ({
  isOpen,
  onClose,
  complaint,
  initialIncludeHistory = true,
  initialIncludeImages = true,
  startWithPreview = false,
  baseUrl = "http://localhost:5000",
}) => {
  const [viewMode, setViewMode] = useState(
    startWithPreview ? "preview" : "choice"
  );
  const [reportType, setReportType] = useState("detailed"); // "detailed" | "slip"
  const [includeHistory, setIncludeHistory] = useState(initialIncludeHistory);
  const [includeImages, setIncludeImages] = useState(initialIncludeImages);

  useEffect(() => {
    if (isOpen) {
      setViewMode(startWithPreview ? "preview" : "choice");
      setIncludeHistory(initialIncludeHistory);
      setIncludeImages(initialIncludeImages);
    }
  }, [isOpen, startWithPreview, initialIncludeHistory, initialIncludeImages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Normalize complaint data
  const data = useMemo(() => {
    if (!complaint) return null;
    const c = complaint.data ? complaint.data : complaint;

    const callNo = c.CallNo || c.callNo || c._id || "N/A";
    const date = c.Date || c.date || c.createdAt || null;
    const complateDate = c.Complate_Date || c.complateDate || c.complate_date || null;
    const status = c.Status || c.status || "Pending";

    // Company
    const company =
      c.CompanyName?.WhaClientName ||
      c.CompanyName?.ClientName ||
      (typeof c.CompanyName === "string" ? c.CompanyName : null) ||
      c.company ||
      "Customer Details";

    // Project
    const project =
      c.Project?.project_name ||
      c.Project?.ProjectName ||
      (typeof c.Project === "string" ? c.Project : null) ||
      c.project ||
      "-";

    // Module
    const module =
      c.Module?.ModuleName ||
      c.Module?.module_name ||
      (typeof c.Module === "string" ? c.Module : null) ||
      c.module ||
      "-";

    // Type
    const type =
      c.Type?.Type_name ||
      c.Type?.TypeName ||
      c.Type?.type_name ||
      (typeof c.Type === "string" ? c.Type : null) ||
      c.type ||
      "-";

    // Generator
    const generatorObj = c.InPutId;
    const generator =
      generatorObj?.Name ||
      generatorObj?.OwnerName ||
      (typeof generatorObj === "string" ? generatorObj : null) ||
      c.employee ||
      "Not Specified";
    const generatorRole = generatorObj?.Role || null;

    // Assigned Support
    const supportObj = c.SuportTeamId;
    const support =
      supportObj?.Name ||
      supportObj?.OwnerName ||
      (typeof supportObj === "string" ? supportObj : null) ||
      c.assignedMember ||
      "Unassigned";
    const supportRole = supportObj?.Role || null;

    // Senior Dev
    const seniorDevObj = c.SeniorDevloperID;
    const seniorDev =
      seniorDevObj?.Name ||
      seniorDevObj?.OwnerName ||
      (typeof seniorDevObj === "string" ? seniorDevObj : null) ||
      c.seniorDeveloper ||
      null;
    const seniorDevStatus =
      c.SeniorDevloperStatus || c.seniorDeveloperStatus || null;

    // Contact
    const contactName = c.ContactPerName || c.contactPerson || null;
    const contactPhone = c.ContactPer || c.contactNumber || null;

    // Scope & Descriptions
    const presentStatus = (c.PresentStatus || c.presentStatus || "").trim();
    const requirement = (c.Requirement || c.requirement || "").trim();
    const solution = (c.Solution || c.solution || "").trim();
    const benefit = (c.Benefit || c.benefit || "").trim();

    // History Records
    const rawRecords = Array.isArray(c.Record)
      ? c.Record
      : Array.isArray(c.records)
      ? c.records
      : [];

    const records = rawRecords.map((r) => {
      const staff =
        r.InHouseId?.Name ||
        r.InHouseId?.OwnerName ||
        (typeof r.InHouseId === "string" ? r.InHouseId : null) ||
        r.partyName ||
        "Team Member";
      const role = r.InHouseId?.Role || r.Role || r.role || null;
      const desc = r.DescripTione || r.description || "";
      const rDate = r.Date || r.date || null;
      const rStatus = r.Status || r.status || "";
      return {
        staff,
        role,
        description: desc,
        date: rDate,
        status: rStatus,
      };
    });

    // Photos
    const rawPhotos = Array.isArray(c.Photos)
      ? c.Photos
      : Array.isArray(c.photos)
      ? c.photos
      : Array.isArray(c.attachments)
      ? c.attachments
      : [];

    const photos = rawPhotos
      .map((p) => {
        const url = typeof p === "string" ? p.trim() : p?.file || p?.url || "";
        return resolveFileUrl(url, baseUrl);
      })
      .filter((url) => Boolean(url) && isJpgOrPng(url));

    const duration = calculateDuration(date, complateDate, status);
    const badge = getStatusBadge(status);

    return {
      callNo,
      date,
      complateDate,
      status,
      company,
      project,
      module,
      type,
      generator,
      generatorRole,
      support,
      supportRole,
      seniorDev,
      seniorDevStatus,
      contactName,
      contactPhone,
      presentStatus,
      requirement,
      solution,
      benefit,
      records,
      photos,
      duration,
      badge,
    };
  }, [complaint, baseUrl]);

  if (!isOpen || !data) return null;

  const cleanCompanyName = (data.company || "Company").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  );
  const documentTitle = `Call_Report_${cleanCompanyName}_Call_${data.callNo}`;

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = documentTitle;
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    }, 150);
  };

  const handleSelectChoice = (type) => {
    setReportType(type);
    setViewMode("preview");
  };

  const hasPresent = Boolean(data.presentStatus && data.presentStatus !== "-");
  const hasReq = Boolean(data.requirement && data.requirement !== "-");
  const areSame =
    hasPresent &&
    hasReq &&
    data.presentStatus.toLowerCase() === data.requirement.toLowerCase();

  const hasSol = Boolean(
    data.solution &&
      data.solution !== "-" &&
      data.solution.toLowerCase() !== "null"
  );
  const hasBen = Boolean(
    data.benefit &&
      data.benefit !== "-" &&
      data.benefit.toLowerCase() !== "null"
  );

  const modalContent = (
    <div className="cpe-modal-overlay animate__animated animate__fadeIn animate__faster">
      {/* =========================================================
          VIEW 1: "Choose Report Type to Print / Export" Modal
          ========================================================= */}
      {viewMode === "choice" && (
        <div
          className="cpe-choice-dialog animate__animated animate__zoomIn animate__faster"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="cpe-choice-header">
            <div className="cpe-choice-title-block">
              <div className="cpe-choice-icon-wrap">
                <FaFilePdf className="text-base" />
              </div>
              <div>
                <h3 className="cpe-choice-title">
                  Choose Report Type to Print / Export
                </h3>
                <p className="cpe-choice-subtitle">
                  {data.company} &bull; Call #{data.callNo}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="cpe-choice-close-btn"
              title="Close modal"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="cpe-choice-body">
            {/* Print Content Customization */}
            <div className="cpe-inclusions-card">
              <div className="cpe-inclusions-header">
                <span className="cpe-inclusions-title">
                  Print Content Inclusions:
                </span>
                <span className="cpe-inclusions-hint">
                  Customize content to include
                </span>
              </div>

              <div className="cpe-inclusions-grid">
                {/* Option 1: Assignment History */}
                <div className="cpe-inclusion-box">
                  <div className="cpe-inclusion-top">
                    <FaHistory style={{ color: "#154D75" }} />
                    <span>Assignment History</span>
                  </div>
                  <div className="cpe-toggle-group">
                    <button
                      type="button"
                      onClick={() => setIncludeHistory(true)}
                      className={`cpe-toggle-btn ${
                        includeHistory ? "active" : ""
                      }`}
                    >
                      ✓ With History
                    </button>
                    <button
                      type="button"
                      onClick={() => setIncludeHistory(false)}
                      className={`cpe-toggle-btn ${
                        !includeHistory ? "active" : ""
                      }`}
                    >
                      ✕ Without History
                    </button>
                  </div>
                </div>

                {/* Option 2: Photos */}
                <div className="cpe-inclusion-box">
                  <div className="cpe-inclusion-top">
                    <FaImage style={{ color: "#059669" }} />
                    <span>Photos ({data.photos.length})</span>
                  </div>
                  <div className="cpe-toggle-group">
                    <button
                      type="button"
                      onClick={() => setIncludeImages(true)}
                      className={`cpe-toggle-btn ${
                        includeImages ? "active image-active" : ""
                      }`}
                    >
                      ✓ With Images
                    </button>
                    <button
                      type="button"
                      onClick={() => setIncludeImages(false)}
                      className={`cpe-toggle-btn ${
                        !includeImages ? "active" : ""
                      }`}
                    >
                      ✕ Without Images
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Format Choice List */}
            <div className="cpe-format-choices">
              {/* Detailed Task & Call Record */}
              <button
                type="button"
                onClick={() => handleSelectChoice("detailed")}
                className="cpe-format-card selected"
              >
                <div className="cpe-format-icon">
                  <FaCalendarAlt />
                </div>
                <div className="cpe-format-content">
                  <div className="cpe-format-header">
                    <span className="cpe-format-title">
                      Detailed Task &amp; Call Record
                    </span>
                    <span className="cpe-format-badge">
                      Full Technical Ticket
                    </span>
                  </div>
                  <p className="cpe-format-desc">
                    Includes Ticket Meta Grid, Senior Developer &amp; Contact
                    Strip, Forwarding History Timeline, Requirement, Solution,
                    Benefit, and attached photos.
                  </p>
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "11px",
                      fontWeight: 800,
                      color: "#154D75",
                    }}
                  >
                    <span>View &amp; Print Detailed Record</span>
                    <FaArrowRight style={{ fontSize: "9px" }} />
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="cpe-choice-footer">
            <button
              type="button"
              onClick={onClose}
              className="cpe-btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setViewMode("preview")}
              className="cpe-btn-primary"
            >
              <span>Open Print Preview</span>
              <FaArrowRight style={{ fontSize: "10px" }} />
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode("preview");
                setTimeout(handlePrint, 250);
              }}
              className="cpe-btn-green"
            >
              <FaPrint />
              <span>Direct Print / PDF</span>
            </button>
          </div>
        </div>
      )}

      {/* =========================================================
          VIEW 2: Fullscreen Print Preview Modal with Sticky Top Bar
          ========================================================= */}
      {viewMode === "preview" && (
        <div className="cpe-preview-container">
          {/* Top Sticky Toolbar */}
          <div className="cpe-sticky-toolbar">
            {/* Left: Title & Subtitle */}
            <div className="cpe-toolbar-left">
              <button
                type="button"
                onClick={() => setViewMode("choice")}
                className="cpe-btn-close"
                title="Back to options"
                style={{ width: "28px", height: "28px" }}
              >
                <FaArrowLeft style={{ fontSize: "11px" }} />
              </button>
              <div>
                <h2 className="cpe-toolbar-title">
                  Complaint Report (Call #{data.callNo}) &ndash; {data.company}
                </h2>
                <p className="cpe-toolbar-sub">
                  Logged on {formatDate(data.date)} &bull; Status: {data.status}
                </p>
              </div>
            </div>

            {/* Center: Live Inclusions Toggles */}
            <div className="cpe-toolbar-center">
              <button
                type="button"
                onClick={() => setIncludeHistory(!includeHistory)}
                className={`cpe-toolbar-pill-btn ${
                  includeHistory ? "active" : ""
                }`}
                title="Toggle Assignment History"
              >
                <FaHistory style={{ fontSize: "10px" }} />
                <span>History: {includeHistory ? "With" : "Without"}</span>
              </button>

              <button
                type="button"
                onClick={() => setIncludeImages(!includeImages)}
                className={`cpe-toolbar-pill-btn ${
                  includeImages ? "active image-active" : ""
                }`}
                title="Toggle Photo Attachments"
              >
                <FaImage style={{ fontSize: "10px" }} />
                <span>Images: {includeImages ? "With" : "Without"}</span>
              </button>
            </div>

            {/* Right: Hint + Print + Close */}
            <div className="cpe-toolbar-right">
              <div className="cpe-print-hint">
                <FaInfoCircle />
                <span>Select &quot;Save as PDF&quot; &amp; check &quot;Background graphics&quot;</span>
              </div>

              <button
                type="button"
                onClick={handlePrint}
                className="cpe-btn-print"
                title="Print or Save as PDF"
              >
                <FaPrint />
                <span>Print / Save as PDF</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="cpe-btn-close"
                title="Close preview"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Document Sheet Wrapper */}
          <div className="cpe-sheet-wrapper">
            <div className="cpe-document-sheet">
              {/* Day / Date Header */}
              <div className="print-date-banner">
                <FaCalendarAlt style={{ color: "#154D75", fontSize: "11px" }} />
                <span>{formatFullDate(data.date)}</span>
              </div>

              {/* Detailed Task & Call Record Card */}
              <div className="print-task-card">
                {/* Dark Blue Header Bar */}
                <div className="print-task-header">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span className="print-task-callno">
                      CALL #{data.callNo}
                    </span>
                    <span
                      className={`print-task-status-pill ${data.badge.className}`}
                    >
                      {data.badge.label}
                    </span>
                    {data.duration && (
                      <span
                        style={{
                          fontSize: "8.5px",
                          background: "rgba(255, 255, 255, 0.2)",
                          padding: "1.5px 7px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        Duration: {data.duration}
                      </span>
                    )}
                    {includeImages && data.photos.length > 0 && (
                      <span
                        style={{
                          fontSize: "8.5px",
                          background: "rgba(255, 255, 255, 0.25)",
                          padding: "1.5px 7px",
                          borderRadius: "4px",
                          fontWeight: 700,
                        }}
                      >
                        📷 {data.photos.length}{" "}
                        {data.photos.length === 1 ? "Photo" : "Photos"}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "9px", opacity: 0.95 }}>
                    Logged: {formatDate(data.date)} at {formatTime(data.date)}
                  </div>
                </div>

                {/* Card Body */}
                <div className="print-task-body">
                  {/* 4-Column Meta Grid */}
                  <div className="print-task-grid">
                    <div className="print-grid-item">
                      <span className="print-grid-label">Company Name</span>
                      <span className="print-grid-val">{data.company}</span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">Generated By</span>
                      <span className="print-grid-val">{data.generator}</span>
                      {data.generatorRole && (
                        <span className="print-grid-sub">
                          ({data.generatorRole})
                        </span>
                      )}
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">Generated Time</span>
                      <span className="print-grid-val">
                        {formatDate(data.date)} {formatTime(data.date)}
                      </span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">
                        {data.complateDate ? "Completed On" : "Call Status"}
                      </span>
                      <span className="print-grid-val">
                        {data.complateDate
                          ? `${formatDate(data.complateDate)} ${formatTime(
                              data.complateDate
                            )}`
                          : data.status}
                      </span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">Project</span>
                      <span className="print-grid-val">{data.project}</span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">Module</span>
                      <span className="print-grid-val">{data.module}</span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">Type</span>
                      <span className="print-grid-val">{data.type}</span>
                    </div>

                    <div className="print-grid-item">
                      <span className="print-grid-label">
                        {data.supportRole?.toLowerCase().includes("support")
                          ? "Support Team"
                          : "Employee"}
                      </span>
                      <span className="print-grid-val">{data.support}</span>
                      {data.supportRole && (
                        <span className="print-grid-sub">
                          ({data.supportRole})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Senior Developer Strip */}
                  {data.seniorDev && (
                    <div className="print-dev-strip">
                      <div>
                        <strong style={{ color: "#581c87" }}>
                          Assigned Senior Developer:
                        </strong>{" "}
                        <span style={{ fontWeight: 800 }}>{data.seniorDev}</span>
                      </div>
                      {data.seniorDevStatus && (
                        <span
                          style={{
                            fontSize: "8.5px",
                            fontWeight: 700,
                            background: "#f3e8ff",
                            color: "#6b21a8",
                            padding: "1.5px 6px",
                            borderRadius: "3px",
                            border: "1px solid #d8b4fe",
                          }}
                        >
                          Dev Status: {data.seniorDevStatus}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Contact Person Strip */}
                  {(data.contactName || data.contactPhone) && (
                    <div className="print-contact-strip">
                      <div>
                        <strong style={{ color: "#166534" }}>
                          Contact Person:
                        </strong>{" "}
                        <span>{data.contactName || "N/A"}</span>
                      </div>
                      {data.contactPhone && (
                        <div>
                          <strong style={{ color: "#166534" }}>Phone:</strong>{" "}
                          <span>{data.contactPhone}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assignment & Forwarding History */}
                  {includeHistory && data.records.length > 0 && (
                    <div className="print-history-box">
                      <div className="print-history-title">
                        Assignment &amp; Forwarding History (
                        {data.records.length}{" "}
                        {data.records.length === 1 ? "Record" : "Records"})
                      </div>
                      {data.records.map((rec, rIdx) => (
                        <div key={rIdx} className="print-history-item">
                          <div className="print-step-badge">{rIdx + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "6px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span>
                                <strong>Forwarded / Assigned To:</strong>{" "}
                                {rec.staff} {rec.role && `(${rec.role})`}
                              </span>
                              <span style={{ color: "#64748b" }}>
                                {formatDate(rec.date)} at {formatTime(rec.date)}
                              </span>
                            </div>
                            {rec.description && (
                              <div
                                style={{
                                  color: "#475569",
                                  marginTop: "2px",
                                  fontSize: "8.5px",
                                }}
                              >
                                Remarks: {rec.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Requirement / Issue Description */}
                  <div className="print-desc-box">
                    <div className="print-desc-title">
                      Requirement / Issue Description
                    </div>
                    {hasPresent && hasReq && !areSame ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <div>
                          <span
                            style={{
                              fontSize: "7.5px",
                              fontWeight: 800,
                              color: "#64748b",
                              textTransform: "uppercase",
                              display: "block",
                            }}
                          >
                            Present Status / Issue:
                          </span>
                          <div className="print-desc-content">
                            {data.presentStatus}
                          </div>
                        </div>
                        <div
                          style={{
                            borderTop: "1px solid #f1f5f9",
                            paddingTop: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "7.5px",
                              fontWeight: 800,
                              color: "#64748b",
                              textTransform: "uppercase",
                              display: "block",
                            }}
                          >
                            Specific Requirement:
                          </span>
                          <div className="print-desc-content">
                            {data.requirement}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="print-desc-content">
                        {data.presentStatus ||
                          data.requirement || (
                            <span
                              style={{ color: "#94a3b8", fontStyle: "italic" }}
                            >
                              No requirement details provided.
                            </span>
                          )}
                      </div>
                    )}
                  </div>

                  {/* Solution Box */}
                  {hasSol && (
                    <div className="print-solution-box">
                      <div className="print-solution-title">
                        ✓ Solution / Action Taken
                      </div>
                      <div className="print-solution-content">
                        {data.solution}
                      </div>
                    </div>
                  )}

                  {/* Benefit Box */}
                  {hasBen && (
                    <div className="print-benefit-box">
                      <div className="print-benefit-title">Benefit</div>
                      <div className="print-benefit-content">{data.benefit}</div>
                    </div>
                  )}

                  {/* Attached Photos */}
                  {includeImages && data.photos.length > 0 && (
                    <div className="print-attachments-box">
                      <div className="print-attachments-header">
                        <div className="print-attachments-title">
                          <FaImage
                            style={{ fontSize: "10px", color: "#154D75" }}
                          />
                          <span>
                            Attached Photos ({data.photos.length}{" "}
                            {data.photos.length === 1 ? "Image" : "Images"})
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: "8.5px",
                            color: "#64748b",
                            fontWeight: 600,
                          }}
                        >
                          High Resolution Print View
                        </span>
                      </div>

                      <div className="print-attachments-grid">
                        {data.photos.map((imgUrl, iIdx) => (
                          <div key={iIdx} className="print-attachment-item">
                            <div className="print-attachment-img-container">
                              <img
                                src={imgUrl}
                                alt={`Attachment ${iIdx + 1}`}
                                className="print-attachment-img"
                                crossOrigin="anonymous"
                              />
                            </div>
                            <div className="print-attachment-footer">
                              <span>Photo #{iIdx + 1}</span>
                              <span style={{ color: "#94a3b8" }}>
                                Call #{data.callNo} Attachment
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ComplaintPrintExportModal;
